// src/components/admin/MenuManager.js
import React, {useState, useEffect} from 'react';
import {menuApi} from '../../api/menu';
import {PencilIcon, TrashIcon, PlusIcon, ArrowDownTrayIcon} from '@heroicons/react/24/outline';

const TYPE_LABELS = {
    appetizer: 'Закуски',
    main_dish: 'Основные блюда',
    drink: 'Напитки',
};

const TYPE_ORDER = ['appetizer', 'main_dish', 'drink'];

const MenuManager = ({auth}) => {
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState([]);
    const [settings, setSettings] = useState({is_open: false});
    const [loading, setLoading] = useState(true);

    // Форма
    const [editing, setEditing] = useState(null); // null | item object
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({item_type: 'appetizer', name: '', description: '', sort_order: 0});

    const loadData = async (skipLoading = false) => {
        if (!skipLoading) setLoading(true);
        try {
            const [it, cnt, set] = await Promise.all([
                menuApi.getItems({auth}),
                menuApi.getCounters({auth}),
                menuApi.getSettings({auth}),
            ]);
            setItems(it.data);
            setCounters(cnt.data);
            setSettings(set.data);
        } catch (err) {
            console.error('Ошибка загрузки меню:', err);
            alert('Не удалось загрузить данные меню');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({item_type: 'appetizer', name: '', description: '', sort_order: 0});
        setShowForm(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setForm({
            item_type: item.item_type,
            name: item.name,
            description: item.description || '',
            sort_order: item.sort_order,
        });
        setShowForm(true);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            alert('Введите название');
            return;
        }
        try {
            if (editing) {
                await menuApi.updateItem(editing.id, {
                    name: form.name.trim(),
                    description: form.description || null,
                    sort_order: Number(form.sort_order) || 0,
                }, {auth});
            } else {
                await menuApi.createItem({
                    item_type: form.item_type,
                    name: form.name.trim(),
                    description: form.description || null,
                    sort_order: Number(form.sort_order) || 0,
                }, {auth});
            }
            setShowForm(false);
            await loadData(true);
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сохранения');
        }
    };

    const handleToggleItem = async (item) => {
        try {
            await menuApi.updateItem(item.id, {is_enabled: !item.is_enabled}, {auth});
            await loadData(true);
        } catch (err) {
            alert('Ошибка изменения доступности');
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Удалить «${item.name}»?`)) return;
        try {
            await menuApi.deleteItem(item.id, {auth});
            await loadData(true);
        } catch (err) {
            alert('Ошибка удаления');
        }
    };

    const handleToggleOpen = async () => {
        try {
            await menuApi.updateSettings({is_open: !settings.is_open}, {auth});
            await loadData(true);
        } catch (err) {
            alert('Ошибка изменения статуса опроса');
        }
    };

    const handleExport = async () => {
        try {
            const res = await menuApi.exportSelections({auth});
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'menu_selections.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Не удалось выгрузить файл');
        }
    };

    if (loading) return <div className="text-center py-4">Загрузка меню...</div>;

    const grouped = TYPE_ORDER.map(type => ({
        type,
        items: items.filter(i => i.item_type === type),
    }));

    const counterByName = (name) => {
        const row = counters.find(c => c.name === name);
        return row ? row.count : 0;
    };

    const counterHeader = (type) => {
        const map = {appetizer: 'Закуска', main_dish: 'Основное блюдо', drink: 'Напиток'};
        return map[type];
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="text-2xl">Меню (опрос гостей)</h2>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleExport}
                        className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
                        title="Выгрузить выборы гостей в XLSX"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4"/> Выгрузить выборы
                    </button>
                    <span className="text-sm">
                        Статус: {settings.is_open ? '🟢 Опрос активен' : '🔴 Опрос закрыт'}
                    </span>
                    <button
                        onClick={handleToggleOpen}
                        className={`px-4 py-2 rounded text-white ${settings.is_open ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        {settings.is_open ? 'Деактивировать опрос' : 'Активировать опрос'}
                    </button>
                </div>
            </div>

            {/* Счётчики */}
            <h3 className="text-xl mb-3">Счётчик выбранных блюд и напитков</h3>
            <div className="overflow-x-auto mb-8">
                {counters.length === 0 ? (
                    <p className="text-gray-500 text-sm">Пока нет позиций меню</p>
                ) : (
                    TYPE_ORDER.map((type) => {
                        const rows = counters.filter((r) => r.item_type === type);
                        if (rows.length === 0) return null;
                        return (
                            <div key={type} className="mb-5">
                                <h4 className="font-semibold mb-2">{TYPE_LABELS[type]}</h4>
                                <table className="w-full border text-sm">
                                    <thead>
                                    <tr className="bg-gray-100">
                                        <th className="p-2 text-left">Блюдо / напиток</th>
                                        <th className="p-2 text-left">Выбрано раз</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.item_id} className="border-t">
                                            <td className="p-2">{row.name}</td>
                                            <td className="p-2 font-semibold">{row.count}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Списки позиций по типам */}
            {grouped.map(({type, items}) => (
                <div key={type} className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold">{TYPE_LABELS[type]}</h3>
                        <button
                            onClick={() => { setForm({item_type: type, name: '', description: '', sort_order: 0}); setEditing(null); setShowForm(true); }}
                            className="text-sm bg-gold-accent text-white px-3 py-1 rounded hover:bg-yellow-600 flex items-center gap-1"
                        >
                            <PlusIcon className="h-4 w-4"/> Добавить
                        </button>
                    </div>
                    {items.length === 0 ? (
                        <p className="text-gray-500 text-sm">Нет позиций</p>
                    ) : (
                        <ul className="space-y-2">
                            {items.map((item) => (
                                <li key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                    <div>
                                        <span className="font-medium">{item.name}</span>
                                        {item.description && (
                                            <span className="text-sm text-gray-500 ml-2">{item.description}</span>
                                        )}
                                        {!item.is_enabled && (
                                            <span className="ml-2 text-xs bg-gray-300 text-gray-700 px-2 py-0.5 rounded">скрыт</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-600">
                                            ✓ {counterHeader(item.item_type)}: {counterByName(item.name)}
                                        </span>
                                        <button onClick={() => handleToggleItem(item)} title="Скрыть/показать"
                                                className="text-sm text-gray-500 hover:text-gray-700">
                                            {item.is_enabled ? 'Скрыть' : 'Показать'}
                                        </button>
                                        <button onClick={() => openEdit(item)} className="text-blue-600">
                                            <PencilIcon className="h-5 w-5"/>
                                        </button>
                                        <button onClick={() => handleDelete(item)} className="text-red-600">
                                            <TrashIcon className="h-5 w-5"/>
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}

            {/* Форма создания/редактирования */}
            {showForm && (
                <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                    <h3 className="text-lg mb-3">{editing ? 'Редактировать позицию' : 'Новая позиция'}</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-gray-700 text-sm">Тип</span>
                            <select className="w-full p-2 border rounded mt-1" value={form.item_type}
                                    onChange={(e) => setForm({...form, item_type: e.target.value})}>
                                {TYPE_ORDER.map(t => (
                                    <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-gray-700 text-sm">Порядок</span>
                            <input type="number" className="w-full p-2 border rounded mt-1"
                                   value={form.sort_order}
                                   onChange={(e) => setForm({...form, sort_order: e.target.value})}/>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-gray-700 text-sm">Название</span>
                            <input className="w-full p-2 border rounded mt-1" value={form.name}
                                   onChange={(e) => setForm({...form, name: e.target.value})}
                                   placeholder="Например: Оливье"/>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-gray-700 text-sm">Описание (необязательно)</span>
                            <textarea className="w-full p-2 border rounded mt-1" rows={2}
                                      value={form.description}
                                      onChange={(e) => setForm({...form, description: e.target.value})}
                                      placeholder="Краткое описание блюда/напитка"/>
                        </label>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <button onClick={handleSubmit}
                                className="bg-gold-accent text-white px-5 py-2 rounded hover:bg-yellow-600">
                            {editing ? 'Сохранить' : 'Добавить'}
                        </button>
                        <button onClick={() => setShowForm(false)}
                                className="bg-gray-200 px-5 py-2 rounded hover:bg-gray-300">
                            Отмена
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuManager;